<script setup lang="ts">
// "Scan QR code" in the phone apps: the camera fills the screen behind the page, which
// draws a frame and Cancel over it. Back cancels too (the scan is a history entry).
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { canScan, cancelScan, scanQr } from '../lib/scan'

const emit = defineEmits<{ scanned: [text: string] }>()
const route = useRoute()
const router = useRouter()
const scanning = ref(false)
const error = ref<string | null>(null)

async function scan(): Promise<void> {
  error.value = null
  scanning.value = true
  await router.push({ query: { ...route.query, scan: '1' } })
  let text: string | null = null
  try {
    text = await scanQr()
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
  }
  scanning.value = false
  // Leave the scan's entry before the page acts on what was scanned (it may navigate).
  if (route.query.scan) {
    const { scan: _, ...query } = route.query
    await router.replace({ query })
  }
  if (text) emit('scanned', text)
}

// Back (gesture or button) leaves the scan's history entry.
watch(
  () => route.query.scan,
  (s) => !s && scanning.value && cancelScan(),
)
</script>

<template>
  <template v-if="canScan">
    <button type="button" class="btn-outline w-full" :disabled="scanning" data-testid="scan-qr" @click="scan">
      Scan QR code
    </button>
    <p v-if="error" class="mt-2 border border-signal px-3 py-2 text-sm text-error" data-testid="scan-error">{{ error }}</p>
    <Teleport to="body">
      <div v-if="scanning" class="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 text-white" data-testid="scan-overlay">
        <p class="mono relative z-10 tracking-[0.14em] uppercase">Point at a QR code</p>
        <div class="scan-frame size-64 border-2 border-white" />
        <button type="button" class="btn relative z-10 border-white bg-black/60 text-white" data-testid="scan-cancel" @click="cancelScan">
          Cancel
        </button>
      </div>
    </Teleport>
  </template>
</template>

<style scoped>
/* Dims the camera around the frame. */
.scan-frame {
  box-shadow: 0 0 0 100vmax rgb(0 0 0 / 0.55);
}
</style>
